import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import SEO from "@/components/marketing/SEO";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Sign in — TAXINPHO"
        description="Sign in to your TAXINPHO account."
        canonical="https://taxinpho.co/login"
        noindex
      />
    <div className="min-h-screen flex">
      {/* Left side */}
      <div className="hidden lg:flex flex-1 bg-zinc-900 text-white relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1672152567948-0d3d10da0039?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtaW5pbWFsJTIwb2ZmaWNlJTIwYnVpbGRpbmclMjBleHRlcmlvcnxlbnwwfHx8fDE3ODI2NzM3Njd8MA&ixlib=rb-4.1.0&q=85"
          alt="Office"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Logo className="h-40 w-auto" variant="stacked" />


          <div>
            <p className="label-caps mb-3 text-zinc-300">CA Office OS</p>
            <h1 className="font-display font-black text-5xl tracking-tighter leading-[0.95] mb-6">
              Run your practice<br />from one console.
            </h1>
            <p className="text-zinc-300 max-w-md leading-relaxed">
              Clients, returns, invoices, tasks and a tax-aware AI — together.
              Built for Chartered Accountants and Professionals in India.
            </p>
          </div>
          <div className="font-mono text-xs text-zinc-400">
            [ gst · itr · tds · roc · audit ]
          </div>
        </div>
      </div>

      {/* Right side - form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-10">
            <Logo className="h-12 w-auto" />
          </div>
          <p className="label-caps mb-3">Sign in</p>
          <h2 className="font-display font-black text-3xl tracking-tighter mb-8 text-zinc-900">
            Welcome back.
          </h2>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="label-caps block mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="w-full border border-zinc-300 px-3 py-2.5 focus:outline-none focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 bg-white"
                placeholder="you@firm.com"
              />
            </div>
            <div>
              <label className="label-caps block mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="w-full border border-zinc-300 px-3 py-2.5 focus:outline-none focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 bg-white"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-zinc-900 text-white py-3 font-semibold hover:bg-zinc-800 active:translate-y-[1px] transition-all disabled:opacity-60"
            >
              {loading ? "Signing in..." : "Sign in →"}
            </button>
          </form>

          <p className="mt-8 text-sm text-zinc-600">
            No account yet?{" "}
            <Link to="/register" className="font-semibold text-zinc-900 underline underline-offset-4" data-testid="link-register">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
    </>
  );
}
